
import React from 'react';
import { motion } from 'framer-motion';
import { SubscriptionPlans } from '@/components/subscription/SubscriptionPlans';
import PageHeader from '@/components/layout/PageHeader';
import PolicyLayout from '@/components/policy/PolicyLayout';
import { BadgeIndianRupee, CheckCircle } from 'lucide-react';

const Pricing = () => {
  // Define feature categories for comparison section
  const featureCategories = [
    {
      name: "Learning Tools",
      features: [
        { name: "Adaptive Quiz Assessment", basic: true, pro: true },
        { name: "Adaptive Flash Cards", basic: true, pro: true },
        { name: "Adaptive Study Notes", basic: true, pro: true },
        { name: "Adaptive Current Affairs", basic: true, pro: true },
        { name: "Adaptive Learning Path", basic: false, pro: true },
        { name: "Adaptive Video Learning", basic: false, pro: true },
        { name: "Adaptive Mock Tests", basic: false, pro: true },
        { name: "PYQ Multiplier", basic: false, pro: true },
      ]
    },
    {
      name: "Analytics & Progress Tracking",
      features: [
        { name: "Basic Analytics", basic: true, pro: true },
        { name: "Advanced Analytics", basic: true, pro: true },
        { name: "Skill-gap Analysis", basic: false, pro: true },
        { name: "Comparative Peer Analytics", basic: false, pro: true },
      ]
    },
    {
      name: "AI & Collaboration",
      features: [
        { name: "AI Learning Studio", basic: false, pro: true },
        { name: "Code Playground", basic: false, pro: true },
        { name: "Collaborative Learning", basic: false, pro: true },
        { name: "24/7 AI Mentor", basic: false, pro: true },
      ]
    }
  ];

  return (
    <PolicyLayout>
      <div className="space-y-8">
        <PageHeader
          title="Simple, Transparent Pricing"
          description="Choose the perfect plan for your learning journey. All plans include core features."
          icon={<BadgeIndianRupee className="w-8 h-8 text-primary" />}
        />

        <motion.div 
          className="pt-4 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SubscriptionPlans mode="public" />
        </motion.div>

        {/* Feature comparison table */}
        <motion.div
          className="mt-16 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h2 className="text-2xl font-bold text-center mb-8">Feature Comparison</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 border text-left w-1/2">Features</th>
                  <th className="p-3 border text-center">Basic</th>
                  <th className="p-3 border text-center">Pro</th>
                </tr>
              </thead>
              <tbody>
                {featureCategories.map((category, idx) => (
                  <React.Fragment key={idx}>
                    <tr className="bg-gray-50">
                      <td colSpan={3} className="p-3 border font-medium">{category.name}</td>
                    </tr>
                    {category.features.map((feature, fidx) => (
                      <tr key={`${idx}-${fidx}`}>
                        <td className="p-3 border">{feature.name}</td>
                        <td className="p-3 border text-center">
                          {feature.basic ? 
                            <CheckCircle className="inline h-5 w-5 text-green-500" /> : 
                            <span className="text-gray-300">—</span>}
                        </td>
                        <td className="p-3 border text-center">
                          {feature.pro ? 
                            <CheckCircle className="inline h-5 w-5 text-green-500" /> : 
                            <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              *All prices are exclusive of applicable GST. GST will be added at checkout.
            </p>
          </div>
        </motion.div>
      </div>
    </PolicyLayout>
  );
};

export default Pricing;
